// FirebaseUI uses the old version of firebase, so we need to import the compat version of the auth module.
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import * as firebaseui from 'firebaseui';
import 'firebaseui/dist/firebaseui.css';

// React stuff
import { getBootcampBySlug, getActiveBootcamps } from '@/services/bootcamp';
import type { Bootcamp } from '@/types/bootcamp';
import { Button, Typography } from '@ford/ford-ui-components';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from './_index.module.css';

// Auth service
firebase.initializeApp({
  apiKey: 'AIzaSyC7Y6UtH27Ih7hFMgiTaVOsEuFI3KPMIPg',
  authDomain: 'ford-towing-bootcamp.firebaseapp.com',
  projectId: 'ford-towing-bootcamp',
  storageBucket: 'ford-towing-bootcamp.appspot.com',
  messagingSenderId: '756797076545',
  appId: '1:756797076545:web:3aad0468d4bb4f0d9982c0',
  measurementId: 'G-7S9CSQD6NM',
});

const authForFirebaseUI = firebase.auth();

function SigninScreen() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Bootcamp state
  const [bootcamp, setBootcamp] = useState<Bootcamp | null>(null);
  const [bootcampLoading, setBootcampLoading] = useState(true);
  const [activeBootcamps, setActiveBootcamps] = useState<Bootcamp[]>([]);

  // Get the page type from the URL (e.g., /bootcamp/elkhart/in/login -> "in")
  const pathParts = location.pathname.split('/');
  const pageType = pathParts[pathParts.length - 2]; // e.g., "in", "out", "dashboard"

  // Fetch bootcamp data
  useEffect(() => {
    if (!slug) return;

    setBootcampLoading(true);
    getBootcampBySlug(slug)
      .then(data => {
        setBootcamp(data);
        setBootcampLoading(false);
      })
      .catch(err => {
        console.error('Error fetching bootcamp:', err);
        setBootcampLoading(false);
      });
  }, [slug]);

  // Fetch active bootcamps for "Did you mean?" suggestions
  useEffect(() => {
    if (!bootcamp && !bootcampLoading) {
      getActiveBootcamps().then(setActiveBootcamps).catch(console.error);
    }
  }, [bootcamp, bootcampLoading]);

  // Handle logout
  useEffect(() => {
    if (searchParams.has('logout')) {
      setSearchParams({});
      firebase
        .auth()
        .signOut()
        .then(() => {
          let newLocation = [...location.pathname.split('/')];
          console.log('newLocation 1', newLocation);
          newLocation.pop();
          console.log('newLocation 2', newLocation);
          newLocation.pop();
          console.log('newLocation 3', newLocation);
          navigate(newLocation.join('/'));
        });
    }
  }, [searchParams, setSearchParams, location.pathname, navigate]);

  // Initialize FirebaseUI - only when bootcamp is loaded and not in loading/error state
  useEffect(() => {
    // Don't initialize if still loading or bootcamp not found
    if (bootcampLoading || !bootcamp) return;

    const ui = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(authForFirebaseUI);
    let signInSuccessUrl = './';

    // see if the user is logged in
    const user = firebase.auth().currentUser;
    if (user) {
      // User is signed in.
      console.log('User is signed in');
      console.log(user);
      // Redirect to the home page
      navigate(signInSuccessUrl);
    }

    ui.start('#firebaseui-auth-container', {
      callbacks: {
        signInSuccessWithAuthResult: function (authResult, redirectUrl) {
          // Action if the user is authenticated successfully
          console.log('authResult', authResult);
          console.log('redirectUrl', redirectUrl);
          return true;
        },
        uiShown: function () {
          // This is what should happen when the form is full loaded. In this example, I hide the loader element.
          const loader = document.getElementById('loader');
          if (loader) loader.style.display = 'none';
        },
      },
      signInSuccessUrl, // This is where should redirect if the sign in is successful.
      signInOptions: [
        // This array contains all the ways an user can authenticate in your application. For this example, is only by email.
        {
          provider: firebase.auth.EmailAuthProvider.PROVIDER_ID,
          signInMethod: firebase.auth.EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD,
          forceSameDevice: false,
          requireDisplayName: false,
          disableSignUp: {
            status: true,
          },
        },
      ],
    });
  }, [bootcampLoading, bootcamp, navigate]);

  // Conditional returns AFTER all hooks (Rules of Hooks compliance)
  // Show loading while fetching bootcamp
  if (bootcampLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>Loading...</div>
      </div>
    );
  }

  // Show not found with suggestions if bootcamp doesn't exist
  if (!bootcamp) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>
          <Typography displayStyle="display-2-small-regular">Bootcamp "{slug}" not found</Typography>
          <br />
          <Typography displayStyle="body-1-regular">Did you mean?</Typography>
          <div className={styles.buttonsContainer}>
            {activeBootcamps.map(b => (
              <Button key={b.slug} onClick={() => (window.location.href = `/bootcamp/${b.slug}/${pageType}/login`)}>
                {b.name}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Login</h1>
      <div id="firebaseui-auth-container"></div>
      <div id="loader" className={styles.loader}>
        Loading
      </div>
    </div>
  );
}
export default SigninScreen;
